import { __decorate } from "tslib";
import { Model, modelRegistry } from './Model';
import { Product, TaxRate, Client, Order } from '.';
import HashGen from '../utilities/HashGen';
import Money from '../utilities/Money';
import Store from '../wrappers/Store';
import { I18n, LocaleOverride } from '../wrappers/I18n';
export class OrderItem extends Model {
    static entity = 'order_items';
    static primaryKey = 'hash';
    id = null;
    hash = '';
    name = '';
    gtin = null;
    unit_of_measure = null;
    discount_percent = '0.00';
    additional_discount_percent = '0.00';
    unit_price = '0.0000';
    tax_rate_id = null;
    tax_rate = '0.00';
    order_id = null;
    order_hash = '';
    ref_order_hash = null;
    ref_order_item_hash = null;
    product_id = null;
    count = '1.000';
    additional_count = '0.000';
    comments = [];
    custom_price = false;
    exempt_from_tax = null;
    reverse_calculation = false;
    created_at = null;
    updated_at = null;
    product;
    order;
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.long()),
            name: this.attr(''),
            gtin: this.attr(null),
            unit_of_measure: this.attr(null),
            discount_percent: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            additional_discount_percent: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            unit_price: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            tax_rate_id: this.attr(null),
            tax_rate: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            order_id: this.attr(null),
            order_hash: this.attr(''),
            ref_order_hash: this.attr(null),
            ref_order_item_hash: this.attr(null),
            product_id: this.attr(null),
            count: this.attr('1.000', value => this.round(value, 3).toFixed(3)),
            additional_count: this.attr('0.000', value => this.round(value, 3).toFixed(3)),
            comments: this.attr([], value => value || []),
            custom_price: this.attr(false, value => !!value),
            exempt_from_tax: this.attr(null),
            reverse_calculation: this.attr(false, value => !!value),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            product: this.attr(undefined, value => value ? new Product(value) : undefined),
            order: this.attr(undefined, value => value ? new Order(value) : undefined),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    static getExemptFromTaxCode(key) {
        return key === 'disabled' ? 'taxes_disabled' : null;
    }
    static exemptFromTaxReasons() {
        const reasonLabel = (reason) => {
            return I18n.t(`order.exempt_from_tax_reason.${reason.code}`);
        };
        let reasons = [
            { code: 'VAT_EXCL', category: null }
        ];
        return reasons.map(r => {
            r.label = reasonLabel(r);
            return r;
        });
    }
    /**
     * Make a copy of the item.
     */
    $duplicate() {
        return new OrderItem(this.$toJson());
    }
    /**
     * Make a fresh copy of the item and link it to the specified order
     */
    fresh(order, reverse = false) {
        const freshItem = this.$duplicate();
        freshItem.id = null;
        freshItem.hash = HashGen.long();
        freshItem.order_id = null;
        freshItem.order_hash = order.hash;
        if (reverse) {
            freshItem.count = this.round(-1 * freshItem.getCount(), 3).toFixed(3);
        }
        return freshItem;
    }
    /**
     * Get Resulting Item (item with additional count and discount)
     */
    getResultingItem() {
        const resultingItem = this.$duplicate();
        resultingItem.count = Money.parse(this.getCount() + this.getAdditionalCount()).toFormat('0.000');
        resultingItem.additional_count = '0.000';
        const resultingDiscountPercent = (1 - (1 - this.getDiscountPercent() / 100) * (1 - this.getAdditionalDiscountPercent() / 100)) * 100;
        resultingItem.discount_percent = resultingDiscountPercent.toFixed(6);
        resultingItem.additional_discount_percent = '0.00';
        return resultingItem;
    }
    /**
     * Give item discount.
     */
    giveDiscount(percent) {
        if (percent < 0 || percent > 99)
            return;
        this.discount_percent = this.round(percent, 2).toFixed(2);
    }
    /**
     * Give additional item discount. Resets additional count
     */
    giveAdditionalDiscount(percent) {
        if (percent < 0 || percent > 99)
            return;
        if (percent != 0) {
            this.additional_count = '0.000';
        }
        this.additional_discount_percent = this.round(percent, 2).toFixed(2);
    }
    /**
     * Update item count.
     */
    updateCount(count) {
        if (count == 0)
            return;
        this.count = this.round(count, 3).toFixed(3);
    }
    /**
     * Update additional item count. Resets additional item discount
     */
    updateAdditionalCount(count) {
        if (count != 0) {
            this.additional_discount_percent = '0.00';
        }
        this.additional_count = this.round(count, 3).toFixed(3);
    }
    /**
     * Update item price.
     */
    updateUnitPrice(price) {
        if (price <= 0)
            return;
        this.custom_price = true;
        this.unit_price = Money.parse(price).toFormat('0.0000');
    }
    /**
     * Set item comments.
     */
    setComments(comments) {
        this.comments = comments;
    }
    setExemptFromTax(exemptFromTaxReason) {
        const product = this.getProduct();
        if (!product)
            return;
        this.exempt_from_tax = exemptFromTaxReason;
        const exemptFromTaxSetsTaxRate = I18n.c('order.exemptFromTaxSetsTaxRate');
        if (exemptFromTaxReason) {
            this.unit_price = this.getUnitPriceBeforeTax().toFormat('0.0000');
            this.tax_rate = '0.00';
            if (exemptFromTaxSetsTaxRate) {
                const taxRate = exemptFromTaxReason === OrderItem.getExemptFromTaxCode('disabled')
                    ? TaxRate.getTaxesDisabledTaxRate()
                    : TaxRate.getZeroTaxRate();
                if (taxRate) {
                    this.tax_rate_id = taxRate.id;
                }
            }
        }
        else {
            const unitPriceBeforeTax = this.getUnitPriceBeforeTax();
            let taxRate = exemptFromTaxSetsTaxRate
                ? product.getTaxRate()
                : TaxRate.find(this.tax_rate_id);
            const taxesEnabled = Store.get('tenant')?.venue?.taxes_enabled;
            const disabledTaxRate = TaxRate.getTaxesDisabledTaxRate();
            if (!taxesEnabled && disabledTaxRate) {
                taxRate = disabledTaxRate;
            }
            if (!taxesEnabled) {
                this.setForcedExemptFromTax('disabled');
            }
            this.tax_rate = this.exempt_from_tax || !taxRate ? '0.00' : taxRate.rate;
            this.unit_price = this.reverse_calculation
                ? unitPriceBeforeTax.toFormat('0.0000')
                : unitPriceBeforeTax.multiply(1 + this.getTaxRate() / 100).toFormat('0.0000');
            if (exemptFromTaxSetsTaxRate && taxRate) {
                this.tax_rate_id = taxRate.id;
            }
        }
    }
    /**
     * Get forced exempt from tax
     */
    setForcedExemptFromTax(reason) {
        const newExemptFromTax = OrderItem.getExemptFromTaxCode(reason);
        if (newExemptFromTax) {
            this.exempt_from_tax = newExemptFromTax;
        }
    }
    /**
     * Check if item has a manually set exempt from task
     */
    hasManuallySetExemptFromTax() {
        return !!this.exempt_from_tax
            && this.exempt_from_tax !== OrderItem.getExemptFromTaxCode('disabled')
            && this.exempt_from_tax !== OrderItem.getExemptFromTaxCode('export')
            && this.exempt_from_tax !== OrderItem.getExemptFromTaxCode('diplomat');
    }
    /**
     * Set Zero Tax
     */
    setZeroTax(reason) {
        if (!this.product_id)
            return;
        this.setForcedExemptFromTax(reason);
        this.unit_price = this.getUnitPriceBeforeTax().toFormat('0.0000');
        const taxRate = TaxRate.getZeroTaxRate();
        this.tax_rate_id = taxRate ? taxRate.id : null;
        this.tax_rate = '0.00';
    }
    /**
     * Set Original Tax
     */
    setOriginalItemsTax() {
        const product = this.getProduct();
        if (!product)
            return;
        const unitPriceBeforeTax = this.getUnitPriceBeforeTax();
        let taxRate = product.getTaxRate();
        const taxesEnabled = Store.get('tenant')?.venue?.taxes_enabled;
        const disabledTaxRate = TaxRate.getTaxesDisabledTaxRate();
        if (!taxesEnabled && disabledTaxRate) {
            taxRate = disabledTaxRate;
        }
        if (!this.hasManuallySetExemptFromTax()) {
            if (!taxesEnabled) {
                this.setForcedExemptFromTax('disabled');
            }
            else {
                this.exempt_from_tax = null;
            }
        }
        this.tax_rate_id = taxRate ? taxRate.id : null;
        this.tax_rate = !this.exempt_from_tax && taxRate ? taxRate.rate : '0.00';
        this.unit_price = this.reverse_calculation
            ? unitPriceBeforeTax.toFormat('0.0000')
            : unitPriceBeforeTax.multiply(1 + this.getTaxRate() / 100).toFormat('0.0000');
    }
    static async createForOrder(order, refOrder) {
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        return new OrderItem({
            hash: HashGen.long(),
            name: await refOrder.getIic(),
            gtin: null,
            unit_of_measure: null,
            order_id: order.id,
            order_hash: order.hash,
            unit_price: refOrder.getToPay().toFormat('0.0000'),
            tax_rate_id: null,
            tax_rate: '0.00',
            product_id: null,
            ref_order_hash: refOrder.hash,
            ref_order_item_hash: null,
            count: '1.000',
            exempt_from_tax: null,
            reverse_calculation: reverseCalculation
        });
    }
    setPricelist(order, pricelist = 0) {
        const client = order.client_id ? Client.find(order.client_id) : null;
        const zeroTaxMode = order.export || client?.isDiplomat();
        const product = this.getProduct();
        if (!product) {
            this.unit_price = '0.0000';
            return;
        }
        this.unit_price = zeroTaxMode
            ? product.getPriceBeforeTax(pricelist).toFormat('0.0000')
            : product.getSellingPrice(pricelist).toFormat('0.0000');
    }
    static createForProduct(order, product, count, discountPercent = 0, pricelist = 0) {
        const client = order.client_id ? Client.find(order.client_id) : null;
        const zeroTaxMode = order.export || client?.isDiplomat();
        const tenant = Store.get('tenant');
        const taxesEnabled = tenant?.venue?.taxes_enabled;
        const reverseCalculation = !!tenant?.venue?.settings.reverse_calculation;
        let taxRate = product.getTaxRate();
        let exemptFromTax = null;
        if (zeroTaxMode) {
            taxRate = TaxRate.getZeroTaxRate();
            exemptFromTax = OrderItem.getExemptFromTaxCode(client?.isDiplomat() ? 'diplomat' : 'export');
        }
        else if (!taxesEnabled) {
            const disabledTaxRate = TaxRate.getTaxesDisabledTaxRate();
            if (disabledTaxRate) {
                taxRate = disabledTaxRate;
            }
            exemptFromTax = OrderItem.getExemptFromTaxCode('disabled');
        }
        return new OrderItem({
            hash: HashGen.long(),
            name: product.name,
            gtin: product.gtin,
            unit_of_measure: product.unit_of_measure_sold,
            discount_percent: discountPercent.toFixed(2),
            order_id: order.id,
            order_hash: order.hash,
            unit_price: zeroTaxMode
                ? product.getPriceBeforeTax(pricelist).toFormat('0.0000')
                : product.getSellingPrice(pricelist).toFormat('0.0000'),
            tax_rate_id: taxRate ? taxRate.id : null,
            tax_rate: exemptFromTax || !taxRate ? '0.00' : taxRate.rate,
            product_id: product.id,
            ref_order_hash: null,
            ref_order_item_hash: null,
            count: Money.parse(count).toFormat('0.000'),
            exempt_from_tax: exemptFromTax,
            reverse_calculation: reverseCalculation
        });
    }
    static createForTaxItem(order, taxItem) {
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        const priceBeforeTax = Money.parse(taxItem.price_before_tax);
        const value = Money.parse(taxItem.value);
        return new OrderItem({
            hash: HashGen.long(),
            name: taxItem.name ? `${taxItem.name} (${taxItem.label})` : taxItem.label,
            gtin: null,
            unit_of_measure: 'pcs',
            order_id: order.id,
            order_hash: order.hash,
            unit_price: reverseCalculation ? priceBeforeTax.toFormat('0.0000') : priceBeforeTax.add(value).toFormat('0.0000'),
            tax_rate_id: taxItem.id,
            tax_rate: taxItem.rate,
            product_id: null,
            ref_order_hash: null,
            ref_order_item_hash: null,
            count: '1.000',
            exempt_from_tax: null,
            reverse_calculation: reverseCalculation
        });
    }
    static createForAdvanceItem(order, taxItem, paidAdvance) {
        const reverseCalculation = !!Store.get('tenant')?.venue?.settings.reverse_calculation;
        const priceAfterTax = Money.parse(paidAdvance);
        const priceBeforeTax = Money.parse(Money.parse(paidAdvance).divide(1 + taxItem.getRate().toUnit() / 100).toFormat('0.0000'));
        return new OrderItem({
            hash: HashGen.long(),
            name: I18n.t('general.advance') + ' ' + taxItem.label,
            gtin: null,
            unit_of_measure: null,
            order_id: order.id,
            order_hash: order.hash,
            unit_price: reverseCalculation ? priceBeforeTax.toFormat('0.0000') : priceAfterTax.toFormat('0.0000'),
            tax_rate_id: taxItem.id,
            tax_rate: taxItem.rate,
            product_id: null,
            ref_order_hash: null,
            ref_order_item_hash: null,
            count: '-1.000',
            exempt_from_tax: null,
            reverse_calculation: reverseCalculation
        });
    }
    /**
     * Lazy Get Product
     */
    getProduct() {
        const product = Product.find(this.product_id);
        if (product) {
            const taxRate = TaxRate.find(product.tax_rate_id);
            product.tax_rate = taxRate || null;
        }
        return product;
    }
    /**
     * Check if this order item is the same as another item (can be merged with it)
     */
    canMergeWith(item) {
        return this.name === item.name
            && this.gtin === item.gtin
            && this.unit_of_measure === item.unit_of_measure
            && this.discount_percent === item.discount_percent
            && this.additional_discount_percent === item.additional_discount_percent
            && this.unit_price === item.unit_price
            && this.tax_rate_id === item.tax_rate_id
            && this.tax_rate === item.tax_rate
            && this.product_id === item.product_id
            && this.exempt_from_tax === item.exempt_from_tax
            && this.reverse_calculation === item.reverse_calculation;
    }
    /**
     * Merge order items
     */
    static merge(items, order) {
        const mergedItems = [];
        items.forEach(item => {
            const freshItem = item.fresh(order);
            const addedItem = mergedItems.find(i => i.canMergeWith(freshItem));
            if (addedItem) {
                addedItem.count = this.round(addedItem.getCount() + freshItem.getCount(), 3).toFixed(3);
            }
            else {
                mergedItems.push(freshItem);
            }
        });
        return mergedItems;
    }
    getDiscountPercent() {
        return parseFloat(this.discount_percent);
    }
    getAdditionalDiscountPercent() {
        return parseFloat(this.additional_discount_percent);
    }
    getTaxRate() {
        return parseFloat(this.tax_rate);
    }
    getTaxRateLabel() {
        const taxRate = TaxRate.find(this.tax_rate_id);
        return taxRate?.label || '';
    }
    getCount() {
        return parseFloat(this.count);
    }
    getAdditionalCount() {
        return parseFloat(this.additional_count);
    }
    /**
     * Get unit price before discount
     */
    getUnitPrice() {
        return Money.parse(this.unit_price);
    }
    /**
     * Get price before discount
     */
    getPrice() {
        const format = I18n.c('order.priceDecimalPrecision');
        if (this.reverse_calculation) {
            const value = this.getUnitPriceBeforeTax()
                .multiply(this.getCount())
                .multiply(1 + this.getTaxRate() / 100);
            return Money.parse(value.toFormat(format));
        }
        const value = this.getUnitPriceAfterTax().multiply(this.getCount());
        return Money.parse(value.toFormat(format));
    }
    /**
     * Get the unit price (product price after tax and discount) - UPA
     */
    getUnitPriceAfterDiscount() {
        if (this.reverse_calculation) {
            const value = this.getUnitPriceBeforeTax()
                .multiply(1 + this.getTaxRate() / 100)
                .multiply(1 - this.getDiscountPercent() / 100);
            return Money.parse(value.toFormat('0.0000'));
        }
        const value = this.getUnitPriceAfterTax().multiply(1 - this.getDiscountPercent() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    /**
     * Get the unit price with discount before tax
     */
    getUnitPriceAfterDiscountBeforeTax() {
        if (this.reverse_calculation) {
            const value = this.getUnitPriceBeforeTax()
                .multiply(1 - this.getDiscountPercent() / 100);
            return Money.parse(value.toFormat('0.0000'));
        }
        const value = this.getUnitPriceAfterTax()
            .multiply(1 - this.getDiscountPercent() / 100)
            .divide(1 + this.getTaxRate() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    /**
     * Get the unit price before tax - UPB
     */
    getUnitPriceBeforeTax() {
        if (this.reverse_calculation) {
            const value = this.getUnitPrice();
            return Money.parse(value.toFormat('0.0000'));
        }
        const value = this.getUnitPrice()
            .divide(1 + this.getTaxRate() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    /**
     * Get the unit price after tax before discount
     */
    getUnitPriceAfterTax() {
        if (this.reverse_calculation) {
            const value = this.getUnitPrice().multiply(1 + this.getTaxRate() / 100);
            return Money.parse(value.toFormat('0.0000'));
        }
        const value = this.getUnitPrice();
        return Money.parse(value.toFormat('0.0000'));
    }
    /**
     * Get the to pay amount for order item - PA
     */
    getToPay() {
        const format = I18n.c('order.priceDecimalPrecision');
        if (this.reverse_calculation) {
            const value = this.getPriceBeforeTax().add(this.getTax());
            return Money.parse(value.toFormat(format));
        }
        const value = this.getUnitPriceAfterDiscount().multiply(this.getCount());
        return Money.parse(value.toFormat(format));
    }
    /**
     * Get the tax amount - VA
     */
    getTax() {
        if (this.reverse_calculation) {
            const value = this.getPriceBeforeTax().multiply(this.getTaxRate() / 100);
            return Money.parse(value.toFormat('0.0000'));
        }
        const value = this.getToPay().multiply(1 - 1 / (1 + this.getTaxRate() / 100));
        return Money.parse(value.toFormat('0.0000'));
    }
    /**
     * Get the price before tax - PB
     */
    getPriceBeforeTax() {
        if (this.reverse_calculation) {
            const value = this.getUnitPriceBeforeTax()
                .multiply(1 - this.getDiscountPercent() / 100)
                .multiply(this.getCount());
            return Money.parse(value.toFormat('0.0000'));
        }
        const value = this.getToPay().subtract(this.getTax());
        return Money.parse(value.toFormat('0.0000'));
    }
    /**
     * Get the discount amount
     */
    getDiscountAmount() {
        const format = I18n.c('order.priceDecimalPrecision');
        const value = this.getPrice().subtract(this.getToPay());
        return Money.parse(value.toFormat(format));
    }
    /**
     * Get additional to pay amount for order item
     */
    getAdditionalToPay() {
        return this.getAdditionalDiscountPercent() != 0 || this.getAdditionalCount() != 0
            ? this.getResultingItem()
                .getToPay()
                .subtract(this.getToPay())
            : Money.parse(0);
    }
    /**
     * Get additional tax amount
     */
    getAdditionalTax() {
        return this.getAdditionalDiscountPercent() != 0 || this.getAdditionalCount() != 0
            ? this.getResultingItem()
                .getTax()
                .subtract(this.getTax())
            : Money.parse(0);
    }
    /**
     * Get additional price before tax
     */
    getAdditionalPriceBeforeTax() {
        return this.getAdditionalDiscountPercent() != 0 || this.getAdditionalCount() != 0
            ? this.getResultingItem()
                .getPriceBeforeTax()
                .subtract(this.getPriceBeforeTax())
            : Money.parse(0);
    }
    getUnitOfMeasure() {
        if (!this.unit_of_measure)
            return null;
        const translationKey = `products.unit_of_measure.${this.unit_of_measure}`;
        const translatedValue = I18n.t(translationKey);
        return translatedValue !== translationKey ? translatedValue : this.unit_of_measure;
    }
}
__decorate([
    LocaleOverride
], OrderItem.prototype, "getPriceBeforeTax", null);
__decorate([
    LocaleOverride
], OrderItem, "getExemptFromTaxCode", null);
__decorate([
    LocaleOverride
], OrderItem, "exemptFromTaxReasons", null);
export default OrderItem;
modelRegistry.register('OrderItem', OrderItem);
//# sourceMappingURL=OrderItem.js.map